<template>
  <p :class="primaryTextClasses" :title="tooltipText">
    {{ releaseLabel }}
  </p>
  <p :class="secondaryTextClasses" :title="tooltipText">
    Built {{ formattedBuildTime }}<span v-if="shortCommitSha"> · {{ shortCommitSha }}</span>
  </p>
</template>

<script setup lang="ts">
import { buildAppReleaseInfo, formatReleaseDisplayName } from "~/utils/appRelease";

const props = withDefaults(defineProps<{
  tone?: "muted" | "strong" | "auth";
}>(), {
  tone: "muted",
});

const { public: config } = useRuntimeConfig();

// Initially hydrate using the SSR-provided backend release ID so Vue matches the HTML payload exactly.
const release = ref(buildAppReleaseInfo({
  version: config.appVersion,
  releaseId: config.appReleaseId,
  releaseName: config.appReleaseName,
  buildTime: config.appBuildTime,
  commitSha: config.appCommitSha,
}));

onMounted(() => {
  // Post-hydration, safely overwrite the Server's (usually Cloud Run) injected 
  // release numbers with the hardcoded frontend build variables to display the
  // true static GUI payload version.
  if (typeof __FRONTEND_RELEASE__ !== "undefined") {
    release.value = __FRONTEND_RELEASE__;
  }
});

const releaseLabel = computed(() => formatReleaseDisplayName(release.value));

const shortCommitSha = computed(() => release.value.commitSha?.slice(0, 7) || "");

const formattedBuildTime = computed(() => {
  const date = new Date(release.value.buildTime);

  if (Number.isNaN(date.getTime())) {
    return release.value.buildTime;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
});

const primaryTextClasses = computed(() => {
  if (props.tone === "auth") {
    return "font-manrope text-[11px] font-medium leading-tight text-slate-900";
  }

  if (props.tone === "strong") {
    return "font-manrope text-[11px] font-medium leading-tight text-slate-800 dark:text-slate-100";
  }

  return "font-manrope text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300";
});

const secondaryTextClasses = computed(() => {
  if (props.tone === "auth") {
    return "mt-0.5 font-manrope text-[10px] leading-tight text-slate-700";
  }

  if (props.tone === "strong") {
    return "mt-0.5 font-manrope text-[10px] leading-tight text-slate-700 dark:text-slate-300";
  }

  return "mt-0.5 font-manrope text-[10px] leading-tight text-slate-500 dark:text-slate-400";
});

const tooltipText = computed(() => {
  const details = [
    `Version ${release.value.version}`,
    `Release ${release.value.releaseName}`,
    `Built ${release.value.buildTime}`,
  ];

  if (release.value.commitSha) {
    details.push(`Commit ${release.value.commitSha.slice(0, 7)}`);
  }

  return details.join("\n");
});
</script>